/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { agentsBlockOnConnection } from "../funcs/agentsBlockOnConnection.js";
import { agentsCreate } from "../funcs/agentsCreate.js";
import { agentsCreatePolicyGrant } from "../funcs/agentsCreatePolicyGrant.js";
import { agentsDelete } from "../funcs/agentsDelete.js";
import { agentsDeletePolicyGrant } from "../funcs/agentsDeletePolicyGrant.js";
import { agentsGet } from "../funcs/agentsGet.js";
import { agentsList } from "../funcs/agentsList.js";
import { agentsListConnectionBlocks } from "../funcs/agentsListConnectionBlocks.js";
import { agentsListDelegableGrants } from "../funcs/agentsListDelegableGrants.js";
import { agentsListPolicyGrants } from "../funcs/agentsListPolicyGrants.js";
import { agentsListSessions } from "../funcs/agentsListSessions.js";
import { agentsReassign } from "../funcs/agentsReassign.js";
import { agentsRemoveConnectionBlock } from "../funcs/agentsRemoveConnectionBlock.js";
import { agentsRename } from "../funcs/agentsRename.js";
import { agentsResume } from "../funcs/agentsResume.js";
import { agentsRevoke } from "../funcs/agentsRevoke.js";
import { agentsRevokeSession } from "../funcs/agentsRevokeSession.js";
import { agentsSuspend } from "../funcs/agentsSuspend.js";
import { agentsTransfer } from "../funcs/agentsTransfer.js";
import { agentsUpdatePolicyGrant } from "../funcs/agentsUpdatePolicyGrant.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AgentConnectionBlock } from "../models/components/agentconnectionblock.js";
import { AgentPolicyGrant } from "../models/components/agentpolicygrant.js";
import { AgentPolicyGrantForm } from "../models/components/agentpolicygrantform.js";
import { ManagedAgent } from "../models/components/managedagent.js";
import {
  BlockAgentOnConnectionRequest,
  BlockAgentOnConnectionSecurity,
} from "../models/operations/blockagentonconnection.js";
import {
  CreateAgentRequest,
  CreateAgentSecurity,
} from "../models/operations/createagent.js";
import {
  CreateAgentPolicyGrantRequest,
  CreateAgentPolicyGrantSecurity,
} from "../models/operations/createagentpolicygrant.js";
import {
  DeleteAgentRequest,
  DeleteAgentSecurity,
} from "../models/operations/deleteagent.js";
import {
  DeleteAgentPolicyGrantRequest,
  DeleteAgentPolicyGrantSecurity,
} from "../models/operations/deleteagentpolicygrant.js";
import {
  GetAgentRequest,
  GetAgentSecurity,
} from "../models/operations/getagent.js";
import {
  ListAgentConnectionBlocksRequest,
  ListAgentConnectionBlocksResponse,
  ListAgentConnectionBlocksSecurity,
} from "../models/operations/listagentconnectionblocks.js";
import {
  ListAgentDelegableGrantsRequest,
  ListAgentDelegableGrantsSecurity,
} from "../models/operations/listagentdelegablegrants.js";
import {
  ListAgentPolicyGrantsRequest,
  ListAgentPolicyGrantsSecurity,
} from "../models/operations/listagentpolicygrants.js";
import {
  ListAgentsRequest,
  ListAgentsResponse,
  ListAgentsSecurity,
} from "../models/operations/listagents.js";
import {
  ListAgentSessionsRequest,
  ListAgentSessionsResponse,
  ListAgentSessionsSecurity,
} from "../models/operations/listagentsessions.js";
import {
  ReassignAgentRequest,
  ReassignAgentSecurity,
} from "../models/operations/reassignagent.js";
import {
  RemoveAgentConnectionBlockRequest,
  RemoveAgentConnectionBlockSecurity,
} from "../models/operations/removeagentconnectionblock.js";
import {
  RenameAgentRequest,
  RenameAgentSecurity,
} from "../models/operations/renameagent.js";
import {
  ResumeAgentRequest,
  ResumeAgentSecurity,
} from "../models/operations/resumeagent.js";
import {
  RevokeAgentRequest,
  RevokeAgentSecurity,
} from "../models/operations/revokeagent.js";
import {
  RevokeAgentSessionRequest,
  RevokeAgentSessionSecurity,
} from "../models/operations/revokeagentsession.js";
import {
  SuspendAgentRequest,
  SuspendAgentSecurity,
} from "../models/operations/suspendagent.js";
import {
  TransferAgentRequest,
  TransferAgentSecurity,
} from "../models/operations/transferagent.js";
import {
  UpdateAgentPolicyGrantRequest,
  UpdateAgentPolicyGrantSecurity,
} from "../models/operations/updateagentpolicygrant.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class Agents extends ClientSDK {
  /**
   * blockAgentOnConnection agents
   *
   * @remarks
   * Block one agent in the caller's organization from the caller's own connection through an upstream OAuth client, across every auth client sharing it. Only the connection owner can do this, and it needs no agent management access. Blocks only; it never grants access.
   */
  async blockOnConnection(
    request: BlockAgentOnConnectionRequest,
    security?: BlockAgentOnConnectionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AgentConnectionBlock> {
    return unwrapAsync(agentsBlockOnConnection(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * create agents
   */
  async create(
    request: CreateAgentRequest,
    security?: CreateAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createPolicyGrant agents
   */
  async createPolicyGrant(
    request: CreateAgentPolicyGrantRequest,
    security?: CreateAgentPolicyGrantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AgentPolicyGrant> {
    return unwrapAsync(agentsCreatePolicyGrant(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * delete agents
   */
  async delete(
    request: DeleteAgentRequest,
    security?: DeleteAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(agentsDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deletePolicyGrant agents
   */
  async deletePolicyGrant(
    request: DeleteAgentPolicyGrantRequest,
    security?: DeleteAgentPolicyGrantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(agentsDeletePolicyGrant(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * get agents
   */
  async get(
    request: GetAgentRequest,
    security?: GetAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * list agents
   */
  async list(
    request?: ListAgentsRequest | undefined,
    security?: ListAgentsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListAgentsResponse, { cursor: string }>> {
    return unwrapResultIterator(agentsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listConnectionBlocks agents
   *
   * @remarks
   * List the agents the caller blocked from their own connections. Needs no agent management access.
   */
  async listConnectionBlocks(
    request?: ListAgentConnectionBlocksRequest | undefined,
    security?: ListAgentConnectionBlocksSecurity | undefined,
    options?: RequestOptions,
  ): Promise<
    PageIterator<ListAgentConnectionBlocksResponse, { cursor: string }>
  > {
    return unwrapResultIterator(agentsListConnectionBlocks(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listDelegableGrants agents
   *
   * @remarks
   * List safe allow-only credential grant candidates shared by the live agent, owner, and current authorizer. Candidates with unrepresentable exclusions are conservatively omitted. Issuance revalidates every grant.
   */
  async listDelegableGrants(
    request: ListAgentDelegableGrantsRequest,
    security?: ListAgentDelegableGrantsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Array<AgentPolicyGrantForm>> {
    return unwrapAsync(agentsListDelegableGrants(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listPolicyGrants agents
   */
  async listPolicyGrants(
    request: ListAgentPolicyGrantsRequest,
    security?: ListAgentPolicyGrantsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Array<AgentPolicyGrant>> {
    return unwrapAsync(agentsListPolicyGrants(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSessions agents
   */
  async listSessions(
    request: ListAgentSessionsRequest,
    security?: ListAgentSessionsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListAgentSessionsResponse, { cursor: string }>> {
    return unwrapResultIterator(agentsListSessions(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * reassign agents
   */
  async reassign(
    request: ReassignAgentRequest,
    security?: ReassignAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsReassign(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * removeConnectionBlock agents
   *
   * @remarks
   * Remove a block the caller wrote on their own connection. Blocks written by an agent manager are not affected.
   */
  async removeConnectionBlock(
    request: RemoveAgentConnectionBlockRequest,
    security?: RemoveAgentConnectionBlockSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(agentsRemoveConnectionBlock(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * rename agents
   */
  async rename(
    request: RenameAgentRequest,
    security?: RenameAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsRename(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * resume agents
   */
  async resume(
    request: ResumeAgentRequest,
    security?: ResumeAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsResume(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * revoke agents
   */
  async revoke(
    request: RevokeAgentRequest,
    security?: RevokeAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsRevoke(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * revokeSession agents
   */
  async revokeSession(
    request: RevokeAgentSessionRequest,
    security?: RevokeAgentSessionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(agentsRevokeSession(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * suspend agents
   */
  async suspend(
    request: SuspendAgentRequest,
    security?: SuspendAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsSuspend(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * transfer agents
   */
  async transfer(
    request: TransferAgentRequest,
    security?: TransferAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ManagedAgent> {
    return unwrapAsync(agentsTransfer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updatePolicyGrant agents
   */
  async updatePolicyGrant(
    request: UpdateAgentPolicyGrantRequest,
    security?: UpdateAgentPolicyGrantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AgentPolicyGrant> {
    return unwrapAsync(agentsUpdatePolicyGrant(
      this,
      request,
      security,
      options,
    ));
  }
}
